
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  useGetOwnerKPIsQuery, 
  useGetRecentOrdersQuery, 
  useGetPopularItemsQuery 
} from '../../services/api';
import { selectCurrentUser } from '../../store/slices/authSlice';
import { formatCurrency, formatDate, getStatusColor, getStatusText } from '../../utils/helpers';
import { updateFaviconAndTitle, resetToDefault } from '../../utils/favicon';
import ProtectedRoute from '../../components/ProtectedRoute/ProtectedRoute';
import Loading from '../../components/Loading/Loading';
import Error from '../../components/Error/Error';
import './Dashboard.css';

const OwnerDashboard = () => {
  const user = useSelector(selectCurrentUser);
  const { data: kpisData, isLoading: kpisLoading, error: kpisError } = useGetOwnerKPIsQuery();
  const { data: recentOrdersData, isLoading: ordersLoading } = useGetRecentOrdersQuery(10);
  const { data: popularItemsData, isLoading: itemsLoading } = useGetPopularItemsQuery(30);


   useEffect(() => {
    if (user?.restaurant) {
      updateFaviconAndTitle(user.restaurant.logoUrl, `${user.restaurant.name} - Owner`);
    }
  }, [user]);

  // Reset to default when component unmounts
  useEffect(() => {
    return () => {
      resetToDefault();
    };
  }, []);

  if (kpisLoading) {
    return <Loading text="Loading dashboard..." />;
  }

  if (kpisError) {
    return <Error message="Failed to load dashboard data" />;
  }

  const kpis = kpisData?.data;
  const recentOrders = recentOrdersData?.data?.orders || [];
  const popularItems = popularItemsData?.data?.popularItems || [];

  return (
    <ProtectedRoute roles={['OWNER']}>
      <div className="container">
        <div className="dashboard-header">
          <h1>Owner Dashboard</h1>
          <p>Welcome back, {user?.name}</p>
        </div>

        {/* KPIs */}
        <div className="kpis-grid">
          <div className="kpi-card">
            <div className="kpi-icon total-orders">📦</div>
            <div className="kpi-content">
              <h3>{kpis?.totalOrders || 0}</h3>
              <p>Total Orders</p>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon pending-orders">⏱️</div>
            <div className="kpi-content">
              <h3>{kpis?.pendingOrders || 0}</h3>
              <p>Pending Orders</p>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon total-revenue">💰</div>
            <div className="kpi-content">
              <h3>{formatCurrency(kpis?.totalRevenue || 0)}</h3>
              <p>Total Revenue</p>
            </div>
          </div>

          <div className="kpi-card">
            <div className="kpi-icon total-customers">👥</div>
            <div className="kpi-content">
              <h3>{kpis?.totalCustomers || 0}</h3>
              <p>Total Customers</p>
            </div>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Recent Orders */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Recent Orders</h2>
              <Link to="/owner/orders" className="btn btn-secondary">
                View All Orders
              </Link>
            </div>

            {ordersLoading ? (
              <Loading text="Loading orders..." />
            ) : (
              <div className="card">
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Customer</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map((order) => (
                        <tr key={order._id}>
                          <td>
                            <strong>{order.orderNumber}</strong>
                          </td>
                          <td>{order.customerId?.name}</td>
                          <td>{formatCurrency(order.amount)}</td>
                          <td>
                            <span className={`status-badge status-${getStatusColor(order.status)}`}>
                              {getStatusText(order.status)}
                            </span>
                          </td>
                          <td>{formatDate(order.createdAt)}</td>
                        </tr>
                      ))}
                      {recentOrders.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            No orders yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Popular Items */}
          <div className="dashboard-section">
            <div className="section-header">
              <h2>Popular Items</h2>
              <Link to="/owner/menu" className="btn btn-secondary">
                Manage Menu
              </Link>
            </div>

            {itemsLoading ? (
              <Loading text="Loading popular items..." />
            ) : (
              <div className="card">
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Total Sold</th>
                        <th>Revenue</th>
                        <th>Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popularItems.map((item) => (
                        <tr key={item._id}>
                          <td>
                            <div className="menu-item-info">
                              {item.menuItem.imageUrl && (
                                <img 
                                  src={`${item.menuItem.imageUrl}`} 
                                  alt={item.menuItem.name}
                                  className="menu-item-image"
                                />
                              )}
                              <span>{item.menuItem.name}</span>
                            </div>
                          </td>
                          <td>{item.totalQuantity}</td>
                          <td>{formatCurrency(item.totalRevenue)}</td>
                          <td>{item.orderCount}</td>
                        </tr>
                      ))}
                      {popularItems.length === 0 && (
                        <tr>
                          <td colSpan="4" className="text-center text-muted">
                            No popular items data yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2>Quick Actions</h2>
          <div className="actions-grid">
            <Link to="/owner/orders" className="action-card">
              <div className="action-icon">📦</div>
              <h3>Manage Orders</h3>
              <p>View and update order status</p>
            </Link>

            <Link to="/owner/menu" className="action-card">
              <div className="action-icon">🍽️</div>
              <h3>Manage Menu</h3>
              <p>Add, edit, or remove menu items</p>
            </Link>

            <Link to="/owner/customers" className="action-card">
              <div className="action-icon">👥</div>
              <h3>View Customers</h3>
              <p>Browse your customer database</p>
            </Link>

            <Link to="/owner/store" className="action-card">
              <div className="action-icon">⚙️</div>
              <h3>Store Settings</h3>
              <p>Update your restaurant details</p>
            </Link>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default OwnerDashboard;